import React, { useState, useEffect, useRef } from 'react';
import { MosqueFormData, PhotoUploadData } from '../types/mosque';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalAnimation } from '../hooks/useModalAnimation';
import SegmentedControl from './SegmentedControl';
import ButtonSpinner from './ButtonSpinner';
import PhotoUpload from './PhotoUpload';
import { resolvePhotoUploadErrorMessage } from '../utils/photoUploadErrors';
import { Globe, Lock, MapPin, CheckCircle, X } from 'lucide-react';

interface AddMosqueFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MosqueFormData, photos: PhotoUploadData[], submitterName?: string) => Promise<void>;
  selectedLocation?: { lat: number; lng: number } | null;
}

const initialFormData: MosqueFormData = {
  name: '',
  type: 'mosque',
  isPublic: true,
  facilities: {
    menWc: null,
    womenWc: null,
    menWudu: null,
    womenWudu: null,
    womenPrayerArea: null,
  },
  description: '',
  sourceLink: '',
};

const COUNTDOWN_SECONDS = 3;

const AddMosqueForm: React.FC<AddMosqueFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedLocation,
}) => {
  const { t } = useLanguage();
  const { isVisible, isClosing, handleClose } = useModalAnimation({ isOpen, onClose });
  const [formData, setFormData] = useState<MosqueFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [photos, setPhotos] = useState<PhotoUploadData[]>([]);
  const [submitterName, setSubmitterName] = useState('');
  const photosRef = useRef<PhotoUploadData[]>([]);

  const clearPhotos = () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    setPhotos([]);
  };

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubmitSuccess(false);
      setSubmitError(null);
      setFormData(initialFormData);
      setCountdown(COUNTDOWN_SECONDS);
      setSubmitterName('');
      clearPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    };
  }, []);

  // Countdown timer for success modal
  useEffect(() => {
    if (submitSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (submitSuccess && countdown === 0) {
      handleClose();
      setSubmitSuccess(false);
    }
  }, [submitSuccess, countdown, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(
        {
          ...formData,
          sourceLink: formData.sourceLink?.trim() || undefined,
        },
        photos,
        submitterName.trim() || undefined
      );
      setFormData(initialFormData);
      setSubmitterName('');
      clearPhotos();
      setSubmitSuccess(true);
      setCountdown(COUNTDOWN_SECONDS);
    } catch (error) {
      console.error('Failed to add mosque:', error);
      const errorCode =
        error && typeof error === 'object' && 'code' in error
          ? (error as { code?: unknown }).code
          : undefined;

      if (errorCode === 'MOSQUE_CREATED_PHOTO_UPLOAD_FAILED') {
        setSubmitError(t('addMosque.photoUploadFailed'));
      } else {
        setSubmitError(resolvePhotoUploadErrorMessage(t, error, 'addMosque.errorMessage'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFacilityChange = (facility: keyof MosqueFormData['facilities'], value: boolean | null) => {
    setFormData(prev => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [facility]: value,
      },
    }));
  };

  if (!isVisible) return null;

  if (submitSuccess) {
    return (
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
        <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-message">
            <CheckCircle size={48} className="success-icon" />
            <h2>{t('addMosque.successTitle')}</h2>
            <p>{t('addMosque.successMessage')}</p>
            <div className="countdown-indicator">
              <div className="countdown-bar" style={{ width: `${(countdown / COUNTDOWN_SECONDS) * 100}%` }} />
            </div>
            <p className="countdown-text">{t('common.closingIn')} {countdown}s</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('addMosque.title')}</h2>
          <button className="close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {submitError && (
          <div className="error-banner">
            <span>{submitError}</span>
            <button type="button" onClick={() => setSubmitError(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mosque-form">
          <div className="form-content">
            <div className="form-group">
              <label htmlFor="name">{t('addMosque.name')} *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
              <span className="form-hint">{t('addMosque.nameHint')}</span>
            </div>

            <div className="form-group">
              <label htmlFor="type">{t('addMosque.type')} *</label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
              >
                <option value="mosque">{t('mosqueTypes.mosque')}</option>
                <option value="prayer_room">{t('mosqueTypes.prayerRoom')}</option>
                <option value="hired_hall">{t('mosqueTypes.hiredHall')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">{t('addMosque.description')}</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('addMosque.descriptionPlaceholder')}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sourceLink">{t('addMosque.sourceLink')}</label>
              <input
                type="url"
                id="sourceLink"
                value={formData.sourceLink || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceLink: e.target.value }))}
                placeholder={t('addMosque.sourceLinkPlaceholder')}
              />
              <span className="form-hint">{t('addMosque.sourceLinkHint')}</span>
            </div>

            <div className="form-group">
              <label>{t('photos.title')}</label>
              <PhotoUpload
                photos={photos}
                onPhotosChange={setPhotos}
                maxPhotos={5}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <h3>{t('addMosque.access')}</h3>
              <div className="access-toggle">
                <div
                  className="access-slider"
                  style={{ transform: `translateX(${formData.isPublic ? 0 : 100}%)` }}
                />
                <button
                  type="button"
                  className={`facility-btn ${formData.isPublic ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                >
                  <Globe size={16} /> {t('mosqueDetails.public')}
                </button>
                <button
                  type="button"
                  className={`facility-btn ${!formData.isPublic ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                >
                  <Lock size={16} /> {t('mosqueDetails.private')}
                </button>
              </div>
            </div>

            <div className="form-group">
              <h3>{t('addMosque.facilities')}</h3>
              <div className="facilities-segmented">
                {(Object.keys(formData.facilities) as Array<keyof MosqueFormData['facilities']>).map((key) => (
                  <SegmentedControl
                    key={key}
                    label={t(`facilities.${key}`)}
                    value={formData.facilities[key]}
                    onChange={(value) => handleFacilityChange(key, value)}
                  />
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="submitterName">{t('addMosque.submitterName')}</label>
              <input
                type="text"
                id="submitterName"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                maxLength={80}
              />
              <span className="form-hint">{t('addMosque.submitterNameHint')}</span>
            </div>

            {selectedLocation && (
              <div className="location-info">
                <p><MapPin size={14} /> Location: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? <><ButtonSpinner /> {t('common.loading')}...</> : t('addMosque.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMosqueForm;
